import PracticeService, {
  SoulMapDocumentStatus,
} from '@/services/practice-service';
import { Message } from '@/store/useChatStore';
import {
  isWithinSoulMapFailedGrace,
  trackSoulMapFailedSince,
  type SoulMapFailedSince,
} from '@/utils/soulmap-document-view';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

const COMPLETION_POLL_INTERVAL_MS = 5_000;
/**
 * While the document is offered but not confirmed, a typed "yes" on the REST
 * path queues the job without a websocket event, so keep a slow poll going.
 */
const OFFERED_POLL_INTERVAL_MS = 10_000;
// const BACKEND_UNAVAILABLE_POLL_INTERVAL_MS = 30_000;
const MINIMUM_COMPLETION_USER_TURNS = 15;

const ACTIVE_DOCUMENT_STATUSES = new Set<SoulMapDocumentStatus['status']>([
  'recomputing_profile',
  'queued',
  'generating',
  'rendering',
  'checking',
]);

/**
 * Keeps the persistent Soul Map document controls synchronized with the
 * backend. The backend also uses this read to recover completed legacy chats.
 */
export function useSoulMapDocuments(
  practiceId: string,
  messages: Message[],
  enabled = true,
  options?: { pollWhileRefreshing?: boolean }
) {
  const userTurnCount = useMemo(
    () => messages.filter((message) => message.isUser).length,
    [messages]
  );
  const shouldPollForCompletion =
    userTurnCount >= MINIMUM_COMPLETION_USER_TURNS;
  const failedSinceRef = useRef<SoulMapFailedSince | null>(null);

  const query = useQuery({
    queryKey: ['soul-map-documents', practiceId],
    queryFn: () => PracticeService.getSoulMapDocuments(Number(practiceId)),
    enabled: enabled && Boolean(practiceId),
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const pollMs = (query.state.data?.poll_after_seconds ?? 5) * 1000;
      const now = Date.now();
      failedSinceRef.current = trackSoulMapFailedSince(
        failedSinceRef.current,
        query.state.data,
        now
      );

      if (status === 'failed') {
        return isWithinSoulMapFailedGrace(failedSinceRef.current, now)
          ? Math.max(pollMs, COMPLETION_POLL_INTERVAL_MS)
          : false;
      }

      if (
        options?.pollWhileRefreshing &&
        (status === 'expired' || status === 'ready')
      ) {
        return Math.max(pollMs, COMPLETION_POLL_INTERVAL_MS);
      }

      if (
        status === 'ready' ||
        status === 'expired' ||
        status === 'unsupported' ||
        status === 'unavailable'
      ) {
        return false;
      }

      if (status && ACTIVE_DOCUMENT_STATUSES.has(status)) {
        return Math.max(pollMs, COMPLETION_POLL_INTERVAL_MS);
      }
      if (status === 'offered') {
        return OFFERED_POLL_INTERVAL_MS;
      }
      return shouldPollForCompletion ? COMPLETION_POLL_INTERVAL_MS : false;
    },
  });
  const { refetch } = query;

  useEffect(() => {
    if (enabled && shouldPollForCompletion) {
      void refetch();
    }
  }, [enabled, refetch, shouldPollForCompletion]);

  return query;
}
